// Copyright (c) StrangeBee 2023

import React, { ReactElement, useMemo } from 'react';
import { ICONS } from '@Constants/iconsApp';
import { handleNotificationError, openMessage, useLocalMessage } from '@Helpers/';
import { ObservableTypeManage } from '@Organisms/Observable/ObservableTypeManage/ObservableTypeManage';
import { ObjectTypeV1 as ObjectType, ObservableTypeOutput, AdminPermissionsEnum } from 'thehive-sdk';
import { StorageFilterEnum } from '@Hooks/useStoragePage';
import useConfigList from '@Hooks/useConfigList';
import client from '@Client';
import ObservableTypeName from '@Organisms/Observable/ObservableTypeName/ObservableTypeName';
import RouteBuilder from '@Services/RouteBuilder';
import ListProvider from '@Context/ListProvider';
import { RouteType } from '@Types';
import AddButton from '@Atoms/Buttons/AddButton/AddButton';
import { THFilter } from '@Molecules/THFilter/THFilter';
import { THList } from '@Molecules/THList';
import { useListAllFields } from '@Queries/describeQueries';
import { useCurrentUser } from '@Queries/userQueries';
import usePermissions from '@Hooks/usePermissions';
import { computeEventList } from '@Organisms/ObservableType/eventList';
import { Footer } from '@Organisms/ExtendFilterFooter/ExtendFilterFooter';
import { PaginationBrowse } from '@Atoms/Inputs/PaginationBrowse/PaginationBrowse';
import { PaginationSize } from '@Atoms/Inputs/PaginationSize/PaginationSize';
import { ManageSwitch } from '@Molecules/ManageSwitch/ManageSwitch';
import { DisplayEmptyData } from '@Atoms/Display/DisplayEmptyData/DisplayEmptyData';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { ManageViewList } from '@Molecules/ManageViewList/ManageViewList';
import { useDrawer } from '@Hooks/useDrawer';

import './ObservableTypeList.scss';

const ObservableTypeList = (): ReactElement => {
    const i = useLocalMessage();

    const { visible: visibleCreate, setVisible: setVisibleCreate } = useDrawer({});

    const config = useConfigList<ObservableTypeOutput>({
        selector: {
            head: [{ name: 'listObservableType' }],
            name: 'get-all-observable-types',
            sort: ['+name'],
        },
        controller: client.ObservableTypeController,
        storageId: StorageFilterEnum.OBSERVABLE_TYPE,
        objectType: ObjectType.OBSERVABLE_TYPE,
    });

    const {
        dataSource = [],
        columnsRenderer,
        listRefetch,
        countRefetch,
        status = 'loading',
        filterManager,
        paginationManager,
        storageViewManager,
        storagePageManager,
        table,
    } = config;

    const eventList = useMemo(
        () =>
            computeEventList({
                controller: client.ObservableTypeController,
                locale: i,
                onSuccess: (count, message) => {
                    if (count) {
                        countRefetch();
                    }
                    if (message) {
                        openMessage('success', message);
                    }
                    listRefetch();
                },
                onError: handleNotificationError,
            }),
        [i, listRefetch, countRefetch],
    );

    const { data: currentUser } = useCurrentUser();
    const { permissions = [] } = currentUser || {};

    const hasPermissions = usePermissions({
        permissionsNeeded: [AdminPermissionsEnum.MANAGE_OBSERVABLE_TEMPLATE],
        permissionsList: permissions,
    });

    const columns = columnsRenderer([
        {
            title: [{ label: i('observable-type.list.table.columns.name'), sortField: 'name' }],
            className: 'observable-type-list__column-name',
            render: ({ name, isAttachment }): ReactElement => (
                <ObservableTypeName name={name} isAttachment={isAttachment} />
            ),
        },
        {
            className: 'observable-type-list__column-actions',
            render: ({ _id }) =>
                hasPermissions ? (
                    <ActionsList
                        actionList={{
                            delete: {
                                label: i('general.delete'),
                                confirm: true,
                                message: i('observable-type.list.delete-confirm'),
                                query: () => eventList.onDelete(_id),
                                icon: <ICONS.DeleteIcon />,
                            },
                        }}
                        isIcon
                        scope="single"
                    />
                ) : null,
        },
    ]);

    const { data: describe } = useListAllFields();

    return (
        <>
            <THList>
                <THList.Header>
                    <THList.Slot name="MANAGE">
                        {hasPermissions && (
                            <AddButton
                                label={i('observable-type.list.header.add-profile')}
                                onClick={() => setVisibleCreate(true)}
                            />
                        )}
                        {storagePageManager && storageViewManager ? (
                            <ManageViewList
                                pageManager={storagePageManager}
                                viewManager={storageViewManager}
                                beforeCreate={(content: any): any => {
                                    filterManager.applyFilters();
                                    return { ...content, filters: filterManager.tempFilters };
                                }}
                            />
                        ) : null}
                    </THList.Slot>
                    <THList.Slot name="NAVIGATION">
                        <ManageSwitch
                            label={i('common.filters')}
                            value={filterManager.editFilters}
                            onChange={(editFilters): void => {
                                filterManager.setEditFilters(editFilters);
                            }}
                            icon={<ICONS.FilterOutlined />}
                        />
                    </THList.Slot>
                    <THList.Slot name="CONTENT">
                        {(filterManager.editFilters || !!filterManager.tempFilters.length) && (
                            <THFilter
                                filterConfig={filterManager}
                                describeField={describe?.[ObjectType.OBSERVABLE_TYPE]}
                            >
                                <Footer filterConfig={filterManager} />
                            </THFilter>
                        )}
                    </THList.Slot>
                </THList.Header>

                <THList.Content
                    className="observable-type__wrapper"
                    dataSource={dataSource}
                    columns={columns}
                    rowKey="_id"
                    emptyPlaceholder={
                        <DisplayEmptyData spin={status === 'loading'} label={i('customField.none')}>
                            {hasPermissions && (
                                <THButton type="link" onClick={() => setVisibleCreate(true)}>
                                    {i('observable-type.list.header.add-profile')}
                                </THButton>
                            )}
                        </DisplayEmptyData>
                    }
                />

                <THList.Footer>
                    <THList.Slot name="NAVIGATION">
                        <PaginationBrowse count={table.count} paginationConfig={paginationManager} />
                        <PaginationSize paginationConfig={paginationManager} />
                    </THList.Slot>
                </THList.Footer>
            </THList>
            {visibleCreate && (
                <ObservableTypeManage
                    visible={visibleCreate}
                    onVisibleChange={setVisibleCreate}
                    onFinish={eventList.onCreate}
                />
            )}
        </>
    );
};

const path = RouteBuilder.compute('administration.observables');

export const Route: RouteType = {
    path,
    menu: {
        label: 'mainLayout.adminMenu.observabletypes',
        icon: <ICONS.ObservableIcon />,
    },
    breadcrumb: {
        label: 'breadcrumb.global.observables',
    },
    content: <ObservableTypeList />,
    Context: ListProvider,
    space: ['admin'],
    permissionNeeded: [AdminPermissionsEnum.MANAGE_OBSERVABLE_TEMPLATE],
    exact: true,
};
